import './styles.scss'

import { Color } from '@tiptap/extension-color'
import { Highlight } from '@tiptap/extension-highlight'
import { Link } from '@tiptap/extension-link'
import { TextAlign } from '@tiptap/extension-text-align'
import { TextStyleKit } from '@tiptap/extension-text-style'
import type { Editor } from '@tiptap/react'
import { EditorContent, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useState, useEffect, useRef } from 'react'
import { colors } from '../../colors'
import { BoldIcon } from '../../icons/bold'
import React from 'react'
import clsx from 'clsx'

const extensions = [
    TextStyleKit,
    StarterKit,
    Color,
    Highlight.configure({ multicolor: true }),
    Link.configure({
        openOnClick: false,
        enableClickSelection: true,
        HTMLAttributes: {
            class: 'text-blue-600 underline',
        },
        validate: href => /^https?:\/\//.test(href),
    }),
    TextAlign.configure({
        types: ['heading', 'paragraph'],
    }),
]

export interface CustomVariable {
    label: string
    value: string
}

function MenuBar({ editor, variables, disabled }: { editor: Editor; variables?: CustomVariable[]; disabled?: boolean }) {
    const [showVariablesMenu, setShowVariablesMenu] = useState(false)
    const [showLinkPopup, setShowLinkPopup] = useState(false)
    const [linkUrl, setLinkUrl] = useState('')
    const [linkPosition, setLinkPosition] = useState<{ top: number; left: number } | null>(null)
    const linkPopupRef = useRef<HTMLDivElement>(null)

    const insertVariable = (variable: string) => {
        editor.chain().focus().insertContent(variable).run()
        setShowVariablesMenu(false)
    }

    // Handle click outside to close popup and save
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showLinkPopup && linkPopupRef.current && !linkPopupRef.current.contains(event.target as Node)) {
                handleLinkSubmit()
            }
        }

        if (showLinkPopup) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showLinkPopup, linkUrl])

    // Update popup position on scroll and block scrolling when popup is open
    useEffect(() => {
        const editorElement = editor.view.dom.closest('.tiptap') as HTMLElement

        if (showLinkPopup && editorElement) {
            // Block scrolling by setting overflow to hidden with !important
            editorElement.style.setProperty('overflow', 'hidden', 'important')
        } else if (editorElement) {
            // Restore scrolling when popup closes
            editorElement.style.removeProperty('overflow')
        }

        return () => {
            if (editorElement) {
                // Restore scrolling on cleanup
                editorElement.style.removeProperty('overflow')
            }
        }
    }, [showLinkPopup, editor])

    const editorState = useEditorState({
        editor,
        selector: ctx => {
            const { from, to } = ctx.editor.state.selection
            return {
                isBold: ctx.editor.isActive('bold') ?? false,
                canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
                isItalic: ctx.editor.isActive('italic') ?? false,
                canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
                isStrike: ctx.editor.isActive('strike') ?? false,
                canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
                isUnderline: ctx.editor.isActive('underline') ?? false,
                canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
                canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
                isParagraph: ctx.editor.isActive('paragraph') ?? false,
                isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
                isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
                isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
                isHeading4: ctx.editor.isActive('heading', { level: 4 }) ?? false,
                isHeading5: ctx.editor.isActive('heading', { level: 5 }) ?? false,
                isHeading6: ctx.editor.isActive('heading', { level: 6 }) ?? false,
                isBulletList: ctx.editor.isActive('bulletList') ?? false,
                isOrderedList: ctx.editor.isActive('orderedList') ?? false,
                isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
                isBlockquote: ctx.editor.isActive('blockquote') ?? false,
                canUndo: ctx.editor.can().chain().undo().run() ?? false,
                canRedo: ctx.editor.can().chain().redo().run() ?? false,
                isLink: ctx.editor.isActive('link') ?? false,
                isAlignLeft: ctx.editor.isActive({ textAlign: 'left' }) ?? false,
                isAlignCenter: ctx.editor.isActive({ textAlign: 'center' }) ?? false,
                isAlignRight: ctx.editor.isActive({ textAlign: 'right' }) ?? false,
                isAlignJustify: ctx.editor.isActive({ textAlign: 'justify' }) ?? false,
                hasSelection: from !== to,
            }
        },
    })

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href

        const { view } = editor
        const { from } = view.state.selection
        const coords = view.coordsAtPos(from)

        // Get the main container and editor element positions
        const container = view.dom.closest('.flex.flex-col.gap-4')
        const containerRect = container?.getBoundingClientRect()
        const editorElement = view.dom.closest('.tiptap') as HTMLElement
        const scrollTop = editorElement?.scrollTop || 0

        setLinkUrl(previousUrl || '')
        setLinkPosition({
            top: coords.bottom - (containerRect?.top || 0) - scrollTop + 5,
            left: coords.left - (containerRect?.left || 0)
        })
        setShowLinkPopup(true)
    }

    const handleLinkSubmit = () => {
        if (linkUrl === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
        } else {
            // Clean the URL: trim and remove quotes
            let finalUrl = linkUrl.trim().replace(/^["']|["']$/g, '')
            
            // Ensure the URL has a protocol
            if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://') && !finalUrl.startsWith('mailto:')) {
                finalUrl = 'https://' + finalUrl
            }
            editor.chain().focus().extendMarkRange('link').setLink({ href: finalUrl }).run()
        }
        setShowLinkPopup(false)
        setLinkUrl('')
    }

    const handleLinkCancel = () => {
        setShowLinkPopup(false)
        setLinkUrl('')
    }

    return (
        <div className="control-group relative">
            <div className="button-group flex flex-wrap gap-2 mb-4">
                <button
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    disabled={disabled || !editorState.canBold}
                    className={editorState.isBold ? 'is-active' : ''}
                >
                    <BoldIcon color={disabled ? colors.neutral[300] : (editorState.isBold ? colors.brandPrimary[700] : colors.neutral[500])} size={24} />
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    disabled={disabled || !editorState.canItalic}
                    className={editorState.isItalic ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 7L10 17M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z" stroke={editorState.isItalic ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    disabled={disabled || !editorState.canStrike}
                    className={editorState.isStrike ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 12H18M11 12H13C14.3807 12 15.5 13.1193 15.5 14.5C15.5 15.8807 14.3807 17 13 17H10.8333C9.83827 17 8.98868 16.3771 8.65321 15.5M15.3468 8.5C15.0113 7.62288 14.1617 7 13.1667 7H11C9.97484 7 9.0938 7.61705 8.70802 8.5M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z" stroke={editorState.isStrike ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    disabled={disabled || !editorState.canUnderline}
                    className={editorState.isUnderline ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.5 7V10.5C15.5 12.433 13.933 14 12 14C10.067 14 8.5 12.433 8.5 10.5V7M8 17H16M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z" stroke={editorState.isUnderline ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {/* Link Button */}
                <button
                    onClick={setLink}
                    disabled={disabled || !editorState.hasSelection}
                    className={editorState.isLink ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 13C10.4295 13.5741 10.9774 14.0491 11.6066 14.3929C12.2357 14.7367 12.9315 14.9411 13.6467 14.9923C14.3618 15.0435 15.0796 14.9403 15.7513 14.6897C16.4231 14.4392 17.0331 14.047 17.54 13.54L20.54 10.54C21.4508 9.59695 21.9548 8.33394 21.9434 7.02296C21.932 5.71198 21.4061 4.45791 20.4791 3.53087C19.5521 2.60383 18.298 2.07799 16.987 2.0666C15.676 2.0552 14.413 2.55918 13.47 3.46997L11.75 5.17997M14 11C13.5705 10.4258 13.0226 9.95078 12.3934 9.60703C11.7642 9.26327 11.0685 9.05885 10.3533 9.00763C9.63819 8.95641 8.9204 9.0596 8.24866 9.31018C7.57692 9.56077 6.9669 9.95291 6.45996 10.46L3.45996 13.46C2.54917 14.403 2.04519 15.666 2.05659 16.977C2.06798 18.288 2.59382 19.542 3.52086 20.4691C4.4479 21.3961 5.70197 21.9219 7.01295 21.9333C8.32393 21.9447 9.58694 21.4407 10.53 20.53L12.24 18.82" stroke={editorState.hasSelection ? (editorState.isLink ? colors.brandPrimary[700] : colors.neutral[500]) : colors.neutral[300]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {/* Text Alignment Buttons */}
                <button
                    onClick={() => editor.chain().focus().setTextAlign('left').run()}
                    disabled={disabled}
                    className={editorState.isAlignLeft ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H21M3 12H15M3 18H21" stroke={editorState.isAlignLeft ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().setTextAlign('center').run()}
                    disabled={disabled}
                    className={editorState.isAlignCenter ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H21M8 12H16M3 18H21" stroke={editorState.isAlignCenter ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().setTextAlign('right').run()}
                    disabled={disabled}
                    className={editorState.isAlignRight ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H21M9 12H21M3 18H21" stroke={editorState.isAlignRight ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                    disabled={disabled}
                    className={editorState.isAlignJustify ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H21M3 12H21M3 18H21" stroke={editorState.isAlignJustify ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {/* Text Color Picker */}
                <input
                    type="color"
                    onInput={(event) => editor.chain().focus().setColor((event.target as HTMLInputElement).value).run()}
                    value={editor.getAttributes('textStyle').color || '#000000'}
                    title="Text Color"
                    disabled={disabled}
                    className="w-8 h-8 cursor-pointer border border-neutral-300 rounded"
                />

                {/* Background Color Picker */}
                <input
                    type="color"
                    onInput={(event) => editor.chain().focus().toggleHighlight({ color: (event.target as HTMLInputElement).value }).run()}
                    value={editor.getAttributes('highlight').color || '#ffff00'}
                    title="Background Color"
                    disabled={disabled}
                    className="w-8 h-8 cursor-pointer border border-neutral-300 rounded"
                />

                {/* <button onClick={() => editor.chain().focus().unsetAllMarks().run()}>Clear marks</button>
                <button onClick={() => editor.chain().focus().clearNodes().run()}>Clear nodes</button> */}
                <button
                    onClick={() => editor.chain().focus().setParagraph().run()}
                    disabled={disabled}
                    className={editorState.isParagraph ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H21M3 12H18C18.7956 12 19.5587 12.3161 20.1213 12.8787C20.6839 13.4413 21 14.2044 21 15C21 15.7956 20.6839 16.5587 20.1213 17.1213C19.5587 17.6839 18.7956 18 18 18H14M14 18L16 16M14 18L16 20M3 18H10" stroke="#010C23" stroke-opacity="0.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    disabled={disabled}
                    className={editorState.isHeading1 ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 4V20M18 4V20M10 4V20M11.5 4H4M18 12H10M11.5 20H4M20 20H16M20 4H16" stroke={editorState.isHeading1 ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    disabled={disabled}
                    className={editorState.isHeading2 ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 5V19M17 5V19M10 5V19M11 5H4M17 12H10M11 19H4M19 19H15.5M19 5H15.5" stroke={editorState.isHeading2 ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    disabled={disabled}
                    className={editorState.isHeading3 ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 6V18M16.5 6V18M10 6V18M11 6H4.5M16.5 12H10M11 18H4.5M18.5 18H15M18.5 6H15" stroke={editorState.isHeading3 ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
                    disabled={disabled}
                    className={editorState.isHeading4 ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 7V17M16 7V17M10 7V17M10.5 7H5M16 12H10M10.5 17H5M18 17H15.5M18 7H15.5" stroke={editorState.isHeading4 ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
                    disabled={disabled}
                    className={editorState.isHeading5 ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 8V16M15.5 8V16M10 8V16M10.5 8H5.5M15.5 12H10M10.5 16H5.5M17.5 16H16M17.5 8H16" stroke={editorState.isHeading5 ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
                    disabled={disabled}
                    className={editorState.isHeading6 ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9V15M15 9V15M10 9V15M10.5 9H6M15 12H10M10.5 15H6M17 15H16.5M17 9H16.5" stroke={editorState.isHeading6 ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    disabled={disabled}
                    className={editorState.isBulletList ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 12L9 12M21 6L9 6M21 18L9 18M5 12C5 12.5523 4.55228 13 4 13C3.44772 13 3 12.5523 3 12C3 11.4477 3.44772 11 4 11C4.55228 11 5 11.4477 5 12ZM5 6C5 6.55228 4.55228 7 4 7C3.44772 7 3 6.55228 3 6C3 5.44772 3.44772 5 4 5C4.55228 5 5 5.44772 5 6ZM5 18C5 18.5523 4.55228 19 4 19C3.44772 19 3 18.5523 3 18C3 17.4477 3.44772 17 4 17C4.55228 17 5 17.4477 5 18Z" stroke="#010C23" stroke-opacity="0.5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
                <button
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    disabled={disabled}
                    className={editorState.isOrderedList ? 'is-active' : ''}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 12L9 12M21 6L9 6M21 18L9 18M3 5V7M3 7H5M3 7V9M3 11V13L5 13M3 13L5 11M3 17V19M5 19H3H5Z" stroke={editorState.isOrderedList ? colors.brandPrimary[700] : colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button onClick={() => editor.chain().focus().setHorizontalRule().run()} disabled={disabled}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 12H21" stroke={colors.neutral[500]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button onClick={() => editor.chain().focus().undo().run()} disabled={disabled || !editorState.canUndo}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 14L4 9M4 9L9 4M4 9H15C18.3137 9 21 11.6863 21 15C21 18.3137 18.3137 21 15 21H13" stroke={editorState.canUndo ? colors.neutral[500] : colors.neutral[300]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <button onClick={() => editor.chain().focus().redo().run()} disabled={disabled || !editorState.canRedo}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 14L20 9M20 9L15 4M20 9H9C5.68629 9 3 11.6863 3 15C3 18.3137 5.68629 21 9 21H11" stroke={editorState.canRedo ? colors.neutral[500] : colors.neutral[300]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>

                {variables && variables.length > 0 && (
                    <div className="relative flex-grow flex justify-end">
                        <button
                            onClick={() => setShowVariablesMenu(!showVariablesMenu)}
                            disabled={disabled}
                            className="flex items-center gap-1 px-3 py-1 rounded hover:bg-neutral-100 text-brandPrimary-600 text-label-md font-semibold"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7 7H17M7 12H17M7 17H12M3 5C3 3.89543 3.89543 3 5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5Z" stroke={colors.brandPrimary[600]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Inserisci una variabile
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 9L12 15L18 9" stroke={colors.brandPrimary[600]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {showVariablesMenu && (
                            <div className="absolute top-full right-0 mt-1 bg-white rounded shadow-lg z-10 min-w-[200px]">
                                <div className="py-1">
                                    {variables.map((variable, index) => (
                                        <button
                                            key={index}
                                            onClick={() => insertVariable(variable.value)}
                                            className="w-full text-left px-4 py-2 hover:bg-neutral-100 flex flex-col"
                                        >
                                            <span className="font-medium text-sm">{variable.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Link Popup */}
                {showLinkPopup && linkPosition && (
                    <div
                        ref={linkPopupRef}
                        className="absolute bg-white border border-neutral-300 rounded-lg shadow-lg p-3 z-50 flex gap-2 items-center"
                        style={{
                            top: `${linkPosition.top}px`,
                            left: `${linkPosition.left}px`
                        }}
                    >
                        <input
                            type="text"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            placeholder="Enter URL"
                            className="border border-neutral-300 rounded px-2 py-1 text-sm min-w-[250px]"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleLinkSubmit()
                                } else if (e.key === 'Escape') {
                                    handleLinkCancel()
                                }
                            }}
                            autoFocus
                        />
                        <button
                            onClick={handleLinkSubmit}
                            className="px-3 py-1 bg-brandPrimary-700 text-white rounded text-sm hover:bg-brandPrimary-800"
                        >
                            Save
                        </button>
                        <button
                            onClick={handleLinkCancel}
                            className="px-3 py-1 bg-neutral-200 text-neutral-700 rounded text-sm hover:bg-neutral-300"
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

interface TiptapProps {
    label?: string
    placeholder?: string
    content?: string
    onChangeHtml?: (html: string) => void
    onChangeText?: (text: string) => void
    key?: string
    variables?: CustomVariable[]
    disabled?: boolean
}

export default (props: TiptapProps) => {
    const editor = useEditor({
        extensions,
        content: props.content || '',
        editable: !props.disabled,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML()
            const text = editor.getText()

            if (props.onChangeHtml) {
                props.onChangeHtml(html)
            }
            if (props.onChangeText) {
                props.onChangeText(text)
            }
        },
    })

    // Update editor content when prop changes
    React.useEffect(() => {
        if (editor && props.content !== undefined && props.content !== null) {
            const currentContent = editor.getHTML()
            // Only update if content is different and not just empty paragraph
            if (currentContent !== props.content && props.content !== '<p></p>') {
                editor.commands.setContent(props.content, { emitUpdate: false })
            }
        }
    }, [props.content, editor])

    // Update editor editable state when disabled prop changes
    React.useEffect(() => {
        if (editor) {
            editor.setEditable(!props.disabled)
        }
    }, [props.disabled, editor])

    if (!editor) {
        return null
    }

    return (
        <div className='flex flex-col gap-4 relative' >
            {
                props.label &&
                <span className='input-label'>{props.label}</span>
            }
            {/* <button onClick={() => console.log(editor?.getText())}>html view</button> */}
            <MenuBar editor={editor} variables={props.variables} disabled={props.disabled} />
            <EditorContent content={props.content} className={clsx('tiptap border border-neutral-300 m-0 rounded-xl p-6 h-52 overflow-auto cursor-text', {
                "opacity-50": props.disabled
            })} editor={editor} />
        </div >
    )
}